import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ThemeService, Theme } from '../services/theme.service';
import { WhatsNewService } from '../services/whats-new.service';
import { WhatsNewDialogComponent } from '../components/whats-new-dialog/whats-new-dialog.component';
import { VERSION_INFO } from '../../version';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    imports: [CommonModule, RouterModule, WhatsNewDialogComponent]
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild(WhatsNewDialogComponent) whatsNewDialog!: WhatsNewDialogComponent;
  
  sidebarOpen = true;
  isMobile = false;
  activeRoute = 'sessions';
  themeMenuOpen = false;
  currentTheme: Theme = 'system';
  hasUnseenUpdates = false;
  
  // Version information
  versionInfo = VERSION_INFO;
  
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private themeService: ThemeService,
    private whatsNewService: WhatsNewService
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    this.setInitialSidebarState();
    
    // Subscribe to theme changes
    this.themeService.theme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.currentTheme = theme;
      });
    
    // Subscribe to unseen updates
    this.whatsNewService.hasUnseenUpdates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasUnseen => {
        this.hasUnseenUpdates = hasUnseen;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkScreenSize();
    this.setInitialSidebarState();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  private setInitialSidebarState(): void {
    // On mobile, sidebar starts closed
    // On desktop, sidebar starts open (can be customized based on user preferences)
    if (this.isMobile) {
      this.sidebarOpen = false;
    } else {
      // Check localStorage for user preference, default to open
      const savedState = localStorage.getItem('sidebarOpen');
      this.sidebarOpen = savedState !== null ? JSON.parse(savedState) : true;
    }
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    
    // Save preference for desktop users
    if (!this.isMobile) {
      localStorage.setItem('sidebarOpen', JSON.stringify(this.sidebarOpen));
    }
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
    
    if (!this.isMobile) {
      localStorage.setItem('sidebarOpen', JSON.stringify(this.sidebarOpen));
    }
  }

  setActiveRoute(route: string): void {
    this.activeRoute = route;
    
    // Close sidebar on mobile when navigating
    if (this.isMobile) {
      this.sidebarOpen = false;
    }
    
    // Here you would typically navigate to the actual route
    // this.router.navigate([`/${route}`]);
    console.log(`Navigating to: ${route}`);
  }

  toggleThemeMenu(): void {
    this.themeMenuOpen = !this.themeMenuOpen;
  }

  closeThemeMenu(): void {
    this.themeMenuOpen = false;
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
    this.closeThemeMenu();
  }

  getThemeIcon(): string {
    const effectiveTheme = this.themeService.getEffectiveTheme();
    return effectiveTheme === 'dark' ? 'pi-moon' : 'pi-sun';
  }

  getThemeLabel(theme: Theme): string {
    const labels: Record<Theme, string> = {
      light: 'Light',
      dark: 'Dark',
      system: 'System'
    };
    return labels[theme];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const themeButton = document.querySelector('.theme-button');
    const themeMenu = document.querySelector('.theme-menu');
    
    if (themeButton && themeMenu && 
        !themeButton.contains(target) && 
        !themeMenu.contains(target)) {
      this.closeThemeMenu();
    }
  }

  showWhatsNew(): void {
    this.whatsNewDialog?.show();
  }

  formatBuildDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

}